// apps/admin/src/components/PipelineTrackerUploadSection.tsx
// Founder upload route for the Salestrekker Pipeline Tracker.
//
// Valutten does white-glove onboarding, so the founder uploads a tenant's
// Salestrekker export on the tenant's behalf until Salestrekker gives API
// access. This card calls the founder-only callable
// `pipelineTrackerFounderIngestionAction` in client-functions, which reaches the
// same `ingestSnapshot` as the tenant's own upload tab. Rows land attributed to
// 'Valutten', never to a raw founder uid.
//
// The add-on check below is a display gate only. The real gate is
// `requireFeature` on the server.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle,
  ClipboardList,
  Loader,
  RefreshCw,
  Upload,
} from 'lucide-react';

/**
 * Match the server's 300 second limit. The client default is 70 seconds, which
 * a 26,000 row Task Summary exceeds while BigQuery is still merging, so the
 * card reported deadline-exceeded for an upload the server went on to finish.
 */
const CALLABLE_TIMEOUT_MS = 300_000;
const CALLABLE_NAME = 'pipelineTrackerFounderIngestionAction';

const REPORT_TYPES = [
  { value: 'performance', label: 'Performance' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'sales', label: 'Sales' },
  { value: 'compliance', label: 'Compliance' },
] as const;

type ReportType = (typeof REPORT_TYPES)[number]['value'];

export interface FounderUploadRow {
  uploadId: string;
  asAtDate: string;
  reportType: string;
  state: string;
  parsedRowCount?: number;
  committedRowCount?: number;
  countsVersion?: 2 | null;
  rejectedIssueCount?: number | null;
  rejectedRowCount?: number;
  refusedHeaders?: string[];
  failureCode?: string | null;
  uploadedBy?: string | null;
  uploadedByEmail?: string | null;
  uploaderRole?: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

interface SubmitResult {
  uploadId: string;
  outcome: 'committed' | 'already_ingested';
  parsedRowCount: number;
  committedRowCount: number;
  countsVersion?: 2 | null;
  rejectedIssueCount?: number | null;
  rejectedRowCount: number;
  warnings?: number;
  ambiguousDateColumns?: string[];
}

interface Refusal {
  refusalCode: string | null;
  headers: string[];
  message: string;
}

interface PipelineTrackerUploadSectionProps {
  companyId: string;
}

/**
 * Base64 without blowing the call stack.
 *
 * `String.fromCharCode(...bytes)` on a multi-megabyte export throws
 * "Maximum call stack size exceeded", which reads like a bug in the file
 * rather than in the encoder, so it is chunked.
 *
 * The client's copy spreads the chunk; this tree targets es5 without
 * downlevelIteration, so the same call is made through `apply`.
 */
function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const chunk = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, Array.prototype.slice.call(chunk) as number[]);
  }
  return btoa(binary);
}

/** Today in Australia/Brisbane as YYYY-MM-DD. Brisbane has no daylight saving. */
export function brisbaneTodayIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Firestore timestamps arrive over the callable wire as plain objects, either
 * `{_seconds,_nanoseconds}` or `{seconds,nanoseconds}`, and legacy rows carry an
 * ISO string. Anything else renders as a dash rather than "Invalid Date".
 */
export function toBrisbaneDisplay(value: unknown): string {
  let date: Date | null = null;
  if (typeof value === 'string' && value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  } else if (value && typeof value === 'object') {
    const o = value as { _seconds?: number; seconds?: number; toDate?: () => Date };
    if (typeof o.toDate === 'function') {
      date = o.toDate();
    } else {
      const secs = typeof o._seconds === 'number' ? o._seconds : o.seconds;
      if (typeof secs === 'number') date = new Date(secs * 1000);
    }
  }
  if (!date || Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/** Never render a raw uid or email in the uploader column. */
export function uploaderLabel(row: FounderUploadRow): string {
  return row.uploadedBy === 'Valutten' || row.uploaderRole === 'system'
    ? 'VALUTTEN'
    : 'Tenant';
}

function refusalFrom(err: unknown): Refusal {
  const e = err as {
    message?: string;
    details?: { refusalCode?: string; headers?: string[]; retryable?: boolean };
  };
  const details = e?.details;
  return {
    refusalCode: details?.refusalCode ?? null,
    headers: Array.isArray(details?.headers) ? (details as { headers: string[] }).headers : [],
    message: e?.message || 'The upload was refused.',
  };
}

/**
 * History for one tenant, read through the founder callable. Kept beside the
 * component because it is the only caller.
 */
export function useFounderTrackerUploads(companyId: string) {
  const [uploads, setUploads] = useState<FounderUploadRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, CALLABLE_NAME);
      const res = (await fn({ action: 'listUploadsAsFounder', companyId, limit: 25 })) as {
        data?: { uploads?: FounderUploadRow[] };
      };
      setUploads(Array.isArray(res?.data?.uploads) ? res.data!.uploads! : []);
    } catch (err) {
      const message = (err as { message?: string })?.message || 'Failed to load upload history.';
      console.error('[PipelineTrackerUpload] history load failed:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  return { uploads, loading, error, refresh };
}

const inputClasses =
  'w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500';
const buttonClasses =
  'w-full py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all';

const PipelineTrackerUploadSection: React.FC<PipelineTrackerUploadSectionProps> = ({
  companyId,
}) => {
  const today = useMemo(() => brisbaneTodayIso(), []);

  const [entitlementLoading, setEntitlementLoading] = useState(true);
  const [granted, setGranted] = useState(false);
  const [entitlementError, setEntitlementError] = useState<string | null>(null);

  const [reportType, setReportType] = useState<ReportType>('performance');
  const [asAtDate, setAsAtDate] = useState<string>(today);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const { uploads, loading: historyLoading, error: historyError, refresh } =
    useFounderTrackerUploads(companyId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setEntitlementLoading(true);
      setEntitlementError(null);
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'entitlements', 'config'));
        const data = snap && typeof snap.data === 'function' ? snap.data() : undefined;
        const enabled =
          (data as { addOnFeatures?: { pipeline_tracker?: { enabled?: boolean } } })
            ?.addOnFeatures?.pipeline_tracker?.enabled === true;
        if (!cancelled) setGranted(enabled);
      } catch (err) {
        console.error('[PipelineTrackerUpload] entitlement read failed:', err);
        if (!cancelled) {
          setGranted(false);
          setEntitlementError('Could not read the add-on entitlement for this company.');
        }
      } finally {
        if (!cancelled) setEntitlementLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (granted) {
      refresh();
    }
  }, [granted, refresh]);

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Choose a .csv or .xlsx export first.');
      return;
    }
    setBusy('Reading the file...');
    setResult(null);
    setRefusal(null);
    try {
      const buffer = await file.arrayBuffer();
      const fileBase64 = toBase64(new Uint8Array(buffer));
      setBusy('Uploading and parsing...');
      const fn = httpsCallable(functions, CALLABLE_NAME, { timeout: CALLABLE_TIMEOUT_MS });
      const res = (await fn({
        action: 'submitSnapshotAsFounder',
        companyId,
        reportType,
        asAtDate,
        fileName: file.name,
        fileBase64,
      })) as { data?: SubmitResult };
      const data = res?.data as SubmitResult;
      setResult(data);
      toast.success(
        data?.outcome === 'already_ingested'
          ? 'Already ingested, nothing changed.'
          : `Committed ${data?.committedRowCount ?? 0} rows.`,
      );
    } catch (err) {
      const parsed = refusalFrom(err);
      setRefusal(parsed);
      toast.error('The upload was refused.');
    } finally {
      // A refusal writes a failed manifest too, so the history must refresh on
      // both outcomes or the operator sees "no snapshots" under a red panel.
      await refresh();
      setBusy(null);
    }
  };

  if (entitlementLoading) {
    return (
      <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
        <p className="text-slate-400 text-sm flex items-center gap-2">
          <Loader className="w-4 h-4 animate-spin" />
          Checking the Pipeline Tracker entitlement...
        </p>
      </div>
    );
  }

  if (!granted) {
    return (
      <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
        <h3 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-indigo-400" />
          Pipeline Tracker
        </h3>
        <p className="text-slate-400 text-sm">
          The Pipeline Tracker add-on is not granted to this company, so there is nothing to upload.
        </p>
        {entitlementError && (
          <p className="text-sm text-amber-400 mt-2">{entitlementError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
        <h3 className="text-xl font-semibold mb-1 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-indigo-400" />
          Pipeline Tracker
        </h3>
        <p className="text-slate-400 text-sm mb-2">
          Upload a Salestrekker export on this tenant's behalf. Rows are attributed to Valutten.
        </p>
        <p className="text-slate-500 text-xs mb-6">
          The original file is kept, write-once, under this tenant's pipeline_tracker_originals folder in Storage. No app user can read it.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label
              htmlFor="tracker-report-type"
              className="block text-sm font-medium text-slate-300 mb-1"
            >
              Report type
            </label>
            <select
              id="tracker-report-type"
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              className={inputClasses}
            >
              {REPORT_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="tracker-as-at-date"
              className="block text-sm font-medium text-slate-300 mb-1"
            >
              As at date
            </label>
            <input
              id="tracker-as-at-date"
              type="date"
              value={asAtDate}
              max={today}
              onChange={(e) => setAsAtDate(e.target.value)}
              className={inputClasses}
            />
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="tracker-file" className="block text-sm font-medium text-slate-300 mb-1">
            Export file
          </label>
          <input
            id="tracker-file"
            type="file"
            accept=".csv,.xlsx"
            onChange={(e) => {
              setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
              setResult(null);
              setRefusal(null);
            }}
            className={inputClasses}
          />
        </div>

        {busy && (
          <p className="text-center text-indigo-400 mb-4 flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            {busy}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!!busy || !file}
          className={buttonClasses}
          type="button"
        >
          <span className="flex items-center justify-center gap-2">
            <Upload className="w-4 h-4" />
            {busy ? 'Working...' : 'Upload snapshot'}
          </span>
        </button>

        {result && (
          <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <h4 className="font-semibold text-green-300 flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4" />
              {result.outcome === 'already_ingested'
                ? 'Already ingested: this exact file was previously loaded for this date'
                : 'Snapshot committed'}
            </h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>Parsed rows: {result.parsedRowCount}</li>
              <li>Committed rows: {result.committedRowCount}</li>
              <li>Skipped rows: {result.countsVersion === 2 ? result.rejectedRowCount : 'Legacy count; reprocess to verify'}</li>
              <li className="font-mono text-xs text-slate-400 break-all">
                Upload ID: {result.uploadId}
              </li>
            </ul>
            {(result.warnings ?? 0) > 0 && (
              <div className="mt-3">
                <p className="text-sm text-amber-300">
                  {result.warnings} non-critical field warning{result.warnings === 1 ? '' : 's'}.
                  Affected optional fields were left empty.
                </p>
              </div>
            )}
            {result.ambiguousDateColumns && result.ambiguousDateColumns.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-amber-300 mb-1">Ambiguous date columns</p>
                <div className="flex flex-wrap gap-2">
                  {result.ambiguousDateColumns.map((c) => (
                    <span
                      key={c}
                      className="px-2 py-1 text-xs rounded bg-amber-500/20 border border-amber-500/30 text-amber-200"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {refusal && (
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <h4 className="font-semibold text-red-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Upload refused
            </h4>
            {refusal.refusalCode && (
              <p className="font-mono text-xs text-red-200 mb-2">{refusal.refusalCode}</p>
            )}
            <p className="text-sm text-slate-300 mb-2">{refusal.message}</p>
            {refusal.headers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {refusal.headers.map((h) => (
                  <span
                    key={h}
                    className="px-2 py-1 text-xs rounded bg-red-500/20 border border-red-500/30 text-red-200"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}
            <p className="text-sm text-slate-400">Nothing was saved from this file.</p>
          </div>
        )}
      </div>

      <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-white">Upload history</h4>
          <button
            type="button"
            onClick={refresh}
            disabled={historyLoading}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${historyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {historyError && <p className="text-sm text-red-400 mb-3">{historyError}</p>}

        {uploads.length === 0 && !historyLoading ? (
          <p className="text-sm text-slate-500">No snapshots have been uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700/50">
                  <th className="py-2 pr-4 font-medium">Report</th>
                  <th className="py-2 pr-4 font-medium">As at</th>
                  <th className="py-2 pr-4 font-medium">State</th>
                  <th className="py-2 pr-4 font-medium">Uploaded by</th>
                  <th className="py-2 pr-4 font-medium">Committed</th>
                  <th className="py-2 pr-4 font-medium">Skipped</th>
                  <th className="py-2 pr-4 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((row) => (
                  <tr key={row.uploadId} className="border-b border-slate-800/50 text-slate-300">
                    <td className="py-2 pr-4 capitalize">{row.reportType}</td>
                    <td className="py-2 pr-4">{row.asAtDate}</td>
                    <td className="py-2 pr-4">{row.state}</td>
                    <td className="py-2 pr-4">{uploaderLabel(row)}</td>
                    <td className="py-2 pr-4">{row.committedRowCount ?? 0}</td>
                    <td className="py-2 pr-4">{row.countsVersion === 2 ? row.rejectedRowCount ?? 0 : 'Legacy count'}</td>
                    <td className="py-2 pr-4">{toBrisbaneDisplay(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineTrackerUploadSection;
